import React from 'react';
import { createPortal } from 'react-dom';
import { ZoneLink } from '../../types';

export interface IconLinkPopoverProps {
  /** The clicked icon's real viewport bounding box, from
   *  `getBoundingClientRect()` — already accounts for pan/zoom and any
   *  ancestor CSS scaling, so positioning off it (rather than world x/y)
   *  keeps the popover a constant, readable size regardless of map zoom. */
  rect: DOMRect;
  link: ZoneLink;
  /** Omit when the host hasn't wired up navigation (no `onZoneLinkClick`)
   *  — the popover still shows the description, just without a button that
   *  would otherwise do nothing when pressed. */
  onOpenLink?: () => void;
  onClose: () => void;
}

/**
 * Shown when a clickable icon with a description is clicked in the viewer,
 * instead of navigating immediately — gives the viewer context before they
 * commit to following the link. Icons without a description keep the old
 * immediate-navigate behavior and never show this. Portaled to
 * document.body and positioned `fixed` in viewport space (same approach as
 * the resource/desk detail popovers) so it never shrinks along with the
 * map's own pan/zoom or a "fit to container" ancestor transform.
 */
export const IconLinkPopover: React.FC<IconLinkPopoverProps> = ({
  rect,
  link,
  onOpenLink,
  onClose
}) => {
  const popoverRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (popoverRef.current?.contains(event.target as Node)) return;
      onClose();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  if (typeof document === 'undefined') return null;

  const buttonLabel =
    link.type === 'office' ? `Go to ${link.label || 'office map'}` : 'Open link';

  return createPortal(
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        left: rect.x + rect.width / 2,
        top: rect.y,
        transform: 'translate(-50%, calc(-100% - 12px))',
        zIndex: 50,
        width: 240,
        borderRadius: 14,
        border: 'none',
        overflow: 'hidden',
        background: 'white',
        boxShadow:
          '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
        padding: 12,
        fontFamily: 'Quicksand, sans-serif'
      }}
    >
      {link.label && (
        <p
          style={{
            margin: 0,
            marginBottom: 4,
            fontSize: 12,
            fontWeight: 600,
            color: '#111827'
          }}
        >
          {link.label}
        </p>
      )}
      <p
        style={{
          margin: 0,
          marginBottom: 10,
          fontSize: 12,
          lineHeight: 1.4,
          color: '#374151',
          whiteSpace: 'pre-wrap'
        }}
      >
        {link.description}
      </p>
      {onOpenLink && (
        <button
          type="button"
          onClick={onOpenLink}
          style={{
            width: '100%',
            borderRadius: 6,
            border: 'none',
            background: '#111827',
            color: 'white',
            fontSize: 12,
            fontWeight: 500,
            padding: '6px 10px',
            cursor: 'pointer'
          }}
        >
          {buttonLabel}
        </button>
      )}
    </div>,
    document.body
  );
};
